import api from '../../../services/api';
import type { TFunction } from 'i18next';
import { submitWithOperationalException } from '../../operationalExceptions/operationalExceptionFlow';
import type { AllocationUnit } from '../../../utils/dimensionConversion';
import type { QuantityUnit } from '../../../utils/commercialQuantity';
import {
  type SupplierOrderManualLandedCostCategory,
  SupplierOrderDto,
  SupplierOrderDocumentDto,
  getSupplierOrderDisplayNumber,
  normalizeSupplierOrderDocument,
  normalizeSupplierOrderDocuments,
  normalizeSupplierOrder,
  normalizeSupplierOrders,
} from '../../../dto/supplierOrder';
import {
  normalizeSupplierOrderPZDocuments,
  type SupplierOrderPZDocumentSummaryDto,
} from '../../../dto/pzTransportCandidates';

export type { SupplierOrderDto, SupplierOrderDocumentDto };
export type { SupplierOrderPZDocumentSummaryDto };
export { getSupplierOrderDisplayNumber };

export interface SupplierOrderItemPayload {
  id?: string | null;
  productVariantId?: string | null;
  name: string;
  quantity: number;
  quantityPcs?: number | null;
  quantityM2?: number | null;
  quantityM3?: number | null;
  unit?: SupplierOrderLineUnit;
  purchaseQuantity?: number;
  purchaseUnit?: SupplierOrderLineUnit;
  conversionQuantity?: number;
  conversionUnit?: SupplierOrderAllocationUnit | null;
  width?: number | null;
  height?: number | null;
  length?: number | null;
  thickness?: number | null;
  currency?: string;
  unitCost?: number;
  description?: string | null;
}

export interface SupplierOrderLandedCostPayloadItem {
  category: SupplierOrderManualLandedCostCategory;
  amount: number;
  currency?: string;
  note?: string | null;
}

export interface SupplierOrderPayload {
  orderNumber?: string | null;
  supplierId: string;
  factoryId?: string | null;
  status?: string;
  statusNotes?: string | null;
  currency?: string;
  totalAmount?: number;
  landedCosts?: SupplierOrderLandedCostPayloadItem[];
  landedCostAllocationMethod?: 'QUANTITY';
  useManualNumbering?: boolean;
  manualOrderNumber?: string | null;
  clientOrderNumber?: string | null;
  producer?: string | null;
  factoryLocation?: string | null;
  deliveryLocation?: string | null;
  description?: string | null;
  supplierReference?: string | null;
  proformaNumber?: string | null;
  proformaDate?: string | null;
  invoiceNumber?: string | null;
  invoiceDate?: string | null;
  waybillNumber?: string | null;
  waybillDate?: string | null;
  orderDate?: string;
  expectedDelivery?: string | null;
  actualDelivery?: string | null;
  estimatedCompletionDate?: string | null;
  notes?: string | null;
  accountManagerId?: string | null;
  items?: SupplierOrderItemPayload[];
}

export type SupplierOrderAllocationUnit = AllocationUnit;
export type SupplierOrderLineUnit = QuantityUnit;
export function stripSupplierOrderItemPurchaseFields(item: SupplierOrderItemPayload): SupplierOrderItemPayload {
  const operationalItem: SupplierOrderItemPayload = { ...item };
  delete operationalItem.currency;
  delete operationalItem.unitCost;
  delete (operationalItem as SupplierOrderItemPayload & { totalCost?: number }).totalCost;
  return operationalItem;
}

export function stripSupplierOrderPurchaseFields(payload: SupplierOrderPayload): SupplierOrderPayload {
  const operationalPayload: SupplierOrderPayload = { ...payload };
  delete operationalPayload.currency;
  delete operationalPayload.totalAmount;
  delete operationalPayload.landedCosts;
  delete operationalPayload.landedCostAllocationMethod;

  if (Array.isArray(operationalPayload.items)) {
    operationalPayload.items = operationalPayload.items.map(stripSupplierOrderItemPurchaseFields);
  }

  return operationalPayload;
}

export async function fetchSupplierOrders(query: Record<string, string | undefined> = {}): Promise<SupplierOrderDto[]> {
  const params = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value) params.append(key, value);
  });

  const response = await api.get(`/supplier-orders${params.toString() ? `?${params.toString()}` : ''}`);
  return normalizeSupplierOrders(response.data, 'supplierOrders.list');
}

/**
 * 2026-08-11: the PZ documents already issued off a supplier order — powers
 * the read-only "Dokumenty PZ" card on the ZD's Powiązania tab.
 */
export async function fetchSupplierOrderPZDocuments(
  supplierOrderId: string,
): Promise<SupplierOrderPZDocumentSummaryDto[]> {
  const response = await api.get(`/supplier-orders/${supplierOrderId}/pz-documents`);
  return normalizeSupplierOrderPZDocuments(response.data, 'supplierOrders.pzDocuments');
}

export async function fetchSupplierOrder(id: string): Promise<SupplierOrderDto> {
  const response = await api.get(`/supplier-orders/${id}`);
  return normalizeSupplierOrder(response.data, 'supplierOrders.detail');
}

/**
 * Consistency warnings (purchase price deviation) answer 409 with the shared operational-exception contract. Pass
 * `confirmation` from a page that has `modal`/`t`; a cancelled confirmation
 * returns `null` and nothing was saved.
 */
export interface SupplierOrderSaveConfirmation {
  modal: Parameters<typeof submitWithOperationalException>[2];
  t: TFunction;
}

export async function createSupplierOrder(
  payload: SupplierOrderPayload,
  confirmation?: SupplierOrderSaveConfirmation,
): Promise<SupplierOrderDto | null> {
  const response = confirmation
    ? await submitWithOperationalException(
      (body) => api.post('/supplier-orders', body),
      payload as unknown as Record<string, unknown>,
      confirmation.modal,
      confirmation.t,
    )
    : await api.post('/supplier-orders', payload);
  if (!response) return null;
  return normalizeSupplierOrder(response.data, 'supplierOrders.create');
}

export async function fetchSuggestedSupplierOrderNumber(orderDate?: string): Promise<string> {
  const response = await api.get('/supplier-orders/suggest-number', {
    params: orderDate ? { orderDate } : undefined,
  });
  return response.data.suggestedNumber;
}

export async function updateSupplierOrder(
  id: string,
  payload: SupplierOrderPayload,
  confirmation?: SupplierOrderSaveConfirmation,
): Promise<SupplierOrderDto | null> {
  const response = confirmation
    ? await submitWithOperationalException(
      (body) => api.put(`/supplier-orders/${id}`, body),
      payload as unknown as Record<string, unknown>,
      confirmation.modal,
      confirmation.t,
    )
    : await api.put(`/supplier-orders/${id}`, payload);
  if (!response) return null;
  return normalizeSupplierOrder(response.data, 'supplierOrders.update');
}

export async function deleteSupplierOrder(id: string) {
  await api.delete(`/supplier-orders/${id}`);
}

export async function fetchSupplierOrderDocuments(supplierOrderId: string): Promise<SupplierOrderDocumentDto[]> {
  const response = await api.get(`/supplier-orders/${supplierOrderId}/documents`);
  return normalizeSupplierOrderDocuments(response.data, 'supplierOrders.documents');
}

export async function createSupplierOrderDocument(supplierOrderId: string, values: Record<string, any>): Promise<SupplierOrderDocumentDto> {
  const payload = new FormData();
  payload.append('documentType', values.documentType || 'SUPPLIER_INVOICE');
  payload.append('number', values.number || '');
  payload.append('documentDate', values.documentDate?.format ? values.documentDate.format('YYYY-MM-DD') : values.documentDate || '');
  payload.append('amount', values.amount == null ? '' : String(values.amount));
  payload.append('currency', values.currency || 'PLN');
  payload.append('notes', values.notes || '');

  const file = values.document?.[0]?.originFileObj || values.document?.[0]?.file || null;
  if (file) {
    payload.append('document', file);
  }

  const response = await api.post(`/supplier-orders/${supplierOrderId}/documents`, payload, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return normalizeSupplierOrderDocument(response.data, 'supplierOrders.document.create');
}

export async function deleteSupplierOrderDocument(documentId: string) {
  await api.delete(`/supplier-orders/documents/${documentId}`);
}

export async function downloadSupplierOrderDocumentFile(documentId: string): Promise<Blob> {
  const response = await api.get(`/supplier-orders/documents/${documentId}/download`, {
    responseType: 'blob',
  });
  return response.data;
}

export type SupplierOrderPdfConversionUnit = 'pcs' | 'm2' | 'm3';

export async function downloadSupplierOrderPdf(
  id: string,
  language: 'en' | 'pl',
  conversionUnit?: SupplierOrderPdfConversionUnit,
): Promise<Blob> {
  const params = new URLSearchParams({ lang: language });
  if (conversionUnit) {
    params.set('conversionUnit', conversionUnit);
  }

  const response = await api.get(`/supplier-orders/${id}/pdf?${params.toString()}`, {
    responseType: 'blob',
  });

  return response.data;
}
